import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import mastercard from 'images/payment/mastercard.svg';
import visa from 'images/payment/visa.svg';
import amex from 'images/payment/americanexpress.svg';
import 'styles/components/purchase/CdsPointsPaymentSummary';

import CdsPointsPaymentSummary from './CdsPointsPaymentSummary';

const cardImages = { visa, mastercard, american_express: amex };
const cardLabels = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  american_express: 'American Express',
};

function normalizeCardBrand(cardBrand) {
  const normalized = typeof cardBrand === 'string' ? cardBrand.trim().toLowerCase() : '';
  const alias = normalized.replace(/[\s_-]+/g, '');

  if (alias === 'mc' || alias === 'mastercard') return 'mastercard';
  if (alias === 'amex' || alias === 'americanexpress') return 'american_express';
  if (alias === 'visa') return 'visa';

  return normalized;
}

function getCardLabel(cardBrand, fallbackLabel) {
  if (!cardBrand) return fallbackLabel;
  if (cardLabels[cardBrand]) return cardLabels[cardBrand];

  return cardBrand.replace(/[_-]/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}

/** Render the card identity returned with a credit-card payment. */
function PaymentCardSummary({
  cardBrand,
  cardNumber,
  paymentType,
  usesCdsPoints,
  pointsAmount,
  purchaseStatus,
  accountId,
  token,
}) {
  const { t } = useTranslation('purchase');
  const normalizedBrand = normalizeCardBrand(cardBrand);
  const safeLastFour = typeof cardNumber === 'string' ? cardNumber.trim().slice(-4) : '';
  const cardImage = cardImages[normalizedBrand];
  const cardTypeLabel = t('label.payment_card_summary.credit');
  const cardLabel = getCardLabel(normalizedBrand, t('label.payment_card_summary.card'));

  if (usesCdsPoints === true && purchaseStatus === 'completed' && pointsAmount != null) {
    return (
      <CdsPointsPaymentSummary pointsAmount={pointsAmount} accountId={accountId} token={token} />
    );
  }

  if (paymentType !== 'credit_card' || (!normalizedBrand && !safeLastFour)) {
    return null;
  }

  return (
    <section
      aria-labelledby="payment-card-summary-title"
      className="mt-6 flex w-full flex-col gap-3"
    >
      <h2 id="payment-card-summary-title" className="text-xl font-semibold text-gray-900">
        {t('label.payment_card_summary.title')}
      </h2>
      <div className="flex w-full items-center justify-between rounded-xl bg-white p-3 shadow-sm">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center">
            {cardImage ? (
              <img src={cardImage} alt={cardLabel} className="h-4 w-10 object-contain" />
            ) : (
              <span
                aria-hidden="true"
                className="h-6 w-9 rounded border border-gray-300 bg-gray-100"
              />
            )}
          </div>
          <span className="truncate text-sm text-gray-600">{`${cardLabel} ${cardTypeLabel}`}</span>
        </div>
        <span className="shrink-0 text-right text-sm text-gray-700">**** **** {safeLastFour}</span>
      </div>
    </section>
  );
}

PaymentCardSummary.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
  pointsAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  purchaseStatus: PropTypes.string,
  cardBrand: PropTypes.string,
  cardNumber: PropTypes.string,
  paymentType: PropTypes.string,
  usesCdsPoints: PropTypes.bool,
};

PaymentCardSummary.defaultProps = {
  accountId: null,
  token: null,
  pointsAmount: null,
  purchaseStatus: null,
  cardBrand: null,
  cardNumber: null,
  paymentType: null,
  usesCdsPoints: false,
};

export default PaymentCardSummary;
